import React from 'react';
import { Link } from 'react-router-dom';
import { FaHeartbeat, FaAppleAlt, FaDumbbell, FaMoon, FaBrain, FaUsers, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import SEO, { PageSEO } from '../components/SEO';
import BrandName from '../components/BrandName';
import { useAuth } from '../context/AuthContext';

const txCtx = require.context('../assets/Tranformations', false, /\.(jpeg|jpg|png|webp)$/i);
const TX_IMAGES = txCtx.keys().sort().map(k => txCtx(k)).slice(0, 8);

const Landing = () => {
  const { user } = useAuth();
  const pillars = [
    { icon: FaAppleAlt, title: 'Nutrition', description: 'Personalized meal plans and science-backed dietary guidance', gradient: 'from-success-500 to-success-600' },
    { icon: FaDumbbell, title: 'Exercise', description: 'Custom fitness plans that fit your lifestyle and goals', gradient: 'from-accent-500 to-accent-600' },
    { icon: FaUsers, title: '1:1 Coaching', description: 'Expert coaches to guide and motivate your journey', gradient: 'from-primary-500 to-primary-600' },
    { icon: FaMoon, title: 'Sleep', description: 'Optimize recovery and hormonal balance', gradient: 'from-primary-600 to-primary-700' },
    { icon: FaBrain, title: 'Mental Health', description: 'Support for sustainable behavioral change', gradient: 'from-success-600 to-success-700' },
    { icon: FaHeartbeat, title: 'Lifestyle', description: 'Build lasting healthy habits for life', gradient: 'from-accent-600 to-accent-700' },
  ];

  return (
    <div className="min-h-screen">
      <SEO {...PageSEO.home} />
      {/* Hero Section with Background Image */}
      <section className="relative h-[calc(100vh-64px)] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 via-primary-900/70 to-primary-900/40"></div>
          <img
            src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1920&q=80"
            alt="Health and Fitness"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="w-2 h-2 bg-success-400 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-white">Trusted by 1000+ Members</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight drop-shadow-lg">
              Transform Your Health,
              <span className="block text-success-400 mt-2 drop-shadow-lg">Not Just Your Weight</span>
            </h1>
            
            <p className="text-base md:text-lg lg:text-xl text-gray-100 mb-6 leading-relaxed drop-shadow-md">
              Fitnession combines personalized nutrition, expert-designed workouts, and 1:1 coaching
              to help you build lasting healthy habits — sustainably and effectively.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center bg-accent-500 text-white px-8 py-4 rounded-lg hover:bg-accent-600 transition-all text-lg font-semibold shadow-2xl hover:shadow-accent-500/50 animate-pulse hover:animate-none"
              >
                View Programs
                <FaArrowRight className="ml-2" />
              </Link>
              <Link
                to={user ? '/dashboard' : '/register'}
                className="inline-flex items-center justify-center bg-success-500 text-white px-8 py-4 rounded-lg hover:bg-success-600 transition-all text-lg font-semibold shadow-2xl hover:shadow-success-500/50"
              >
                {user ? 'Go to Dashboard' : 'Start Your Journey'}
                <FaArrowRight className="ml-2" />
              </Link>
              <Link
                to="/our-program"
                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white/20 transition-all text-lg font-semibold"
              >
                Learn More
              </Link>
            </div>

            {/* Trust Badges */}
            {/* <div className="mt-10 flex flex-wrap items-center gap-6">
              <div className="flex items-center space-x-2 text-white/90">
                <FaCheckCircle className="text-success-400" />
                <span className="text-sm">No hidden costs</span>
              </div>
              <div className="flex items-center space-x-2 text-white/90">
                <FaCheckCircle className="text-success-400" />
                <span className="text-sm">30-day money-back</span>
              </div>
              <div className="flex items-center space-x-2 text-white/90">
                <FaCheckCircle className="text-success-400" />
                <span className="text-sm">Cancel anytime</span>
              </div>
            </div> */}
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Happy Members Section - Full Width Image */}
      <section className="relative">
        {/* Full Width Image with Overlay */}
        <div className="relative h-[400px] md:h-[500px] overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1920&q=80"
            alt="Happy Fitnession Members"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/70 via-primary-800/50 to-success-900/70"></div>

          {/* Content Overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white px-4 max-w-4xl">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Join Our Community of
                <span className="text-success-400"> Happy, Healthy</span> Members
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-6 max-w-2xl mx-auto">
                Real people achieving real results. Our members don't just lose weight - they gain confidence, energy, and a whole new outlook on life.
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                <div className="flex items-center space-x-2">
                  <div className="flex -space-x-3">
                    <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop" alt="Member" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                    <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop" alt="Member" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                    <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop" alt="Member" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                    <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop" alt="Member" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                    <div className="w-10 h-10 rounded-full border-2 border-white bg-success-500 flex items-center justify-center text-sm font-bold">
                      +1K
                    </div>
                  </div>
                  <span className="text-white/90 text-sm">Happy Members</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section with Background */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary-600 mb-2">1K+</div>
              <div className="text-gray-600">Members Transformed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-success-600 mb-2">18%</div>
              <div className="text-gray-600">Average Weight Loss</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-accent-600 mb-2">94%</div>
              <div className="text-gray-600">Satisfaction Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary-600 mb-2">85%</div>
              <div className="text-gray-600">Keep Weight Off</div>
            </div>
          </div>
        </div>
      </section>

      {/* Six Pillars Section with Images */}
      <section className="relative py-20 bg-white">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230ea5e9' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Holistic Approach
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Six pillars working together for lasting transformation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${pillar.gradient} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                <div className="relative p-8">
                  <div className={`w-16 h-16 bg-gradient-to-br ${pillar.gradient} rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
                    <pillar.icon className="text-2xl text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    {pillar.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{pillar.description}</p>
                </div>
                <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <pillar.icon className="text-6xl text-gray-900" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Transformations Teaser */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Real Transformations</h2>
            <p className="text-gray-500">Genuine results from our members — no filters, no edits.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {TX_IMAGES.map((img, i) => (
              <div key={i} className="overflow-hidden rounded-2xl aspect-square">
                <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/success-stories"
              className="inline-flex items-center gap-2 bg-primary-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary-700 transition-colors shadow-lg"
            >
              See All Transformations <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section with Background Image */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 to-success-900/90 z-10"></div>
          <img 
            src="https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=1920&q=80" 
            alt="Success"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Change Your Life?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands who have transformed their health with Fitnession
          </p>
          <Link
            to={user ? '/dashboard' : '/register'}
            className="inline-flex items-center bg-white text-primary-600 px-10 py-4 rounded-lg hover:bg-gray-100 transition-all text-lg font-semibold shadow-2xl"
          >
            {user ? 'Go to Dashboard' : 'Get Started Today'}
            <FaArrowRight className="ml-2" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <FaHeartbeat className="text-primary-400 text-2xl" />
            <BrandName className="text-xl" />
          </div>
          <p className="text-gray-400">
            Transforming lives through diet, exercise, and expert coaching
          </p>
          <p className="text-gray-500 mt-4">© 2025 Fitnession. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;